import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, User, X } from "lucide-react";
import MultiFilter from "./MultiFilter";
import dayjs from "dayjs";
import { categoryOf, isCancelled, minutesOf, prettyType } from "./apptTypes";
import { SLOT_MINUTES } from "./ScheduleViews";

/* One hour, read end to end. The grid puts each provider in a column, so a
   busy hour is spread across columns that may be off-screen; clicking the hour
   in the time gutter lists everything in it.

   Laid out as the slots run: anything still going when the hour opened is
   grouped first as "Continuing", then one row per slot, each naming what
   starts in it or saying it is empty. The schedule list carries no end time,
   so a visit is drawn SLOT_MINUTES long, the same length the grid draws - and
   the slot rows step by the same amount, so they line up with the times
   appointments are actually booked at.

   Read-only: a row opens the appointment card the grid opens. */

const hourLabel = (h) => dayjs().hour(h).minute(0).format("h A");
// minutes after midnight -> "h:mm A" by arithmetic (startOf("day").add() is an
// hour off on a DST change day, e.g. 2026-11-01)
const timeLabel = (min) => {
  const h = Math.floor(min / 60) % 24;
  return `${h % 12 || 12}:${String(min % 60).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const nameOf = (a) =>
  (a?.patientName || `${a?.firstname || ""} ${a?.lastname || ""}`).trim() || "Patient";

export const hourOf = (a) => Math.floor(minutesOf(a?.starttime) / 60);
const startOf = (a) => minutesOf(a?.starttime);
const endOf = (a) => startOf(a) + SLOT_MINUTES;

const Row = ({ a, continuing, onOpen, onClose, dimOf, showDay }) => {
  const cat = categoryOf(a.type);
  const off = isCancelled(a);
  const dim = dimOf ? dimOf(a) : false;
  return (
    <button
      type="button"
      className={`bmhr-row${off ? " off" : ""}${dim ? " dim" : ""}`}
      style={{ "--c": cat.color, "--t": cat.tint }}
      onClick={(e) => {
        onOpen(a, e.currentTarget.getBoundingClientRect());
        onClose();
      }}
    >
      <span className="bmhr-who">
        <b>{nameOf(a)}</b>
        <small>
          {showDay && a.startdate ? `${dayjs(a.startdate).format("ddd M/D")} · ` : ""}
          {a.patientid ? `#${a.patientid}` : ""}
          {a.patientid && a.providername ? " · " : ""}
          {a.providername}
        </small>
      </span>
      <span className="bmhr-type">{prettyType(a.type) || cat.label}</span>
      <span className="bmhr-when">
        <b>
          {timeLabel(startOf(a))}–{timeLabel(endOf(a))}
        </b>
        {continuing && <small>Started earlier</small>}
      </span>
      {a.visit_confirmation ? (
        <span className="bmhr-state">{a.visit_confirmation}</span>
      ) : (
        <span className="bmhr-state ghost" aria-hidden="true" />
      )}
    </button>
  );
};

const HourSheet = ({ hour, appts, dateLabel, onOpen, onClose, dimOf, onHour, minHour, maxHour }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (!onHour) return;
      if (e.key === "ArrowLeft" && hour > minHour) onHour(hour - 1);
      if (e.key === "ArrowRight" && hour < maxHour) onHour(hour + 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onHour, hour, minHour, maxHour]);

  /* The sheet is handed the list the calendar is already showing, so whatever
     was filtered there - providers, visit types - is filtered here. This one
     narrows further, within the hour, and stays put as the arrows walk hours. */
  const [only, setOnly] = useState(null);

  const from = hour * 60;
  const to = from + 60;
  const hourAll = useMemo(() => {
    const inHour = (appts || []).filter((a) => startOf(a) >= from && startOf(a) < to);
    const before = (appts || []).filter((a) => startOf(a) < from && endOf(a) > from);
    return { inHour, before };
  }, [appts, from, to]);
  // The week view hands in the whole week: then each row names its day.
  const multiDay = useMemo(
    () => new Set((appts || []).map((a) => dayjs(a.startdate).format("YYYY-MM-DD"))).size > 1,
    [appts],
  );

  // Options off the unfiltered hour, so a provider can always be picked back.
  const providerOptions = useMemo(() => {
    const n = new Map();
    [...hourAll.before, ...hourAll.inHour].forEach((a) => {
      if (a.providername) n.set(a.providername, (n.get(a.providername) || 0) + 1);
    });
    return [...n.entries()]
      .sort((x, y) => x[0].localeCompare(y[0]))
      .map(([value, count]) => ({ value, label: value, count }));
  }, [hourAll]);

  const mine = (a) => only === null || only.includes(a.providername);
  const inHour = hourAll.inHour.filter(mine);
  // Still going when the hour opened: started before it, not finished by it.
  const continuing = hourAll.before
    .filter(mine)
    .sort((a, b) => startOf(a) - startOf(b) || nameOf(a).localeCompare(nameOf(b)));
  const all = [...continuing, ...inHour];

  const providers = new Set(all.map((a) => a.providername).filter(Boolean));
  const types = [];
  all.forEach((a) => {
    const cat = categoryOf(a.type);
    const label = prettyType(a.type) || cat.label;
    const hit = types.find((t) => t.label === label);
    if (hit) hit.n += 1;
    else types.push({ label, n: 1, color: cat.color });
  });
  types.sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));

  const slots = [];
  for (let m = from; m < to; m += SLOT_MINUTES) {
    slots.push({
      m,
      items: inHour
        .filter((a) => startOf(a) >= m && startOf(a) < m + SLOT_MINUTES)
        .sort((a, b) => startOf(a) - startOf(b) || nameOf(a).localeCompare(nameOf(b))),
    });
  }

  const canPrev = Boolean(onHour) && hour > minHour;
  const canNext = Boolean(onHour) && hour < maxHour;

  return (
    <div className="bmhr-layer" onClick={onClose}>
      <div
        className="bmhr"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bmhr-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bmhr-head">
          <span className="bmhr-head-icon" aria-hidden="true">
            <Clock />
          </span>
          <div className="bmhr-head-text">
            <h2 id="bmhr-title">
              {hourLabel(hour)} – {hourLabel((hour + 1) % 24)}
            </h2>
            <p className="bmhr-sub">
              {dateLabel ? `${dateLabel} · ` : ""}
              {all.length} appointment{all.length === 1 ? "" : "s"}
              {providers.size ? ` with ${providers.size} provider${providers.size === 1 ? "" : "s"}` : ""}
            </p>
          </div>
          <div className="bmhr-nav">
            <button
              type="button"
              className="bmhr-icon"
              onClick={() => canPrev && onHour(hour - 1)}
              disabled={!canPrev}
              aria-label="Previous hour"
              title="Previous hour"
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              className="bmhr-icon"
              onClick={() => canNext && onHour(hour + 1)}
              disabled={!canNext}
              aria-label="Next hour"
              title="Next hour"
            >
              <ChevronRight aria-hidden="true" />
            </button>
            <button type="button" className="bmhr-icon" onClick={onClose} aria-label="Close" title="Close">
              <X aria-hidden="true" />
            </button>
          </div>
        </div>

        {(types.length > 0 || providerOptions.length > 1) && (
          <div className="bmhr-types">
            {types.map((t) => (
              <span key={t.label} className="bmhr-chip" style={{ "--c": t.color }}>
                <i aria-hidden="true" />
                {t.label} <b>{t.n}</b>
              </span>
            ))}
            {providerOptions.length > 1 && (
              <div className="bmhr-filter">
                <MultiFilter
                  icon={<User aria-hidden="true" />}
                  caption="Providers"
                  allLabel="All providers"
                  noun="providers"
                  options={providerOptions}
                  selected={only}
                  setSelected={setOnly}
                />
              </div>
            )}
          </div>
        )}

        <div className="bmhr-body">
          {continuing.length > 0 && (
            <div className="bmhr-group">
              <div className="bmhr-group-k">
                <b>Continuing</b>
                <small>from before {hourLabel(hour)}</small>
              </div>
              <div className="bmhr-group-rows">
                {continuing.map((a, i) => (
                  <Row
                    key={`c-${a.appointmentid ?? a.patientid}-${a.startdate}-${a.starttime}-${i}`}
                    a={a}
                    showDay={multiDay}
                    continuing
                    onOpen={onOpen}
                    onClose={onClose}
                    dimOf={dimOf}
                  />
                ))}
              </div>
            </div>
          )}

          {slots.map((s) => (
            <div key={s.m} className={`bmhr-slot${s.items.length ? "" : " empty"}`}>
              <div className="bmhr-slot-k">{timeLabel(s.m)}</div>
              <div className="bmhr-group-rows">
                {s.items.length === 0 ? (
                  <span className="bmhr-none">No appointments</span>
                ) : (
                  s.items.map((a, i) => (
                    <Row
                      key={`${a.appointmentid ?? a.patientid}-${a.startdate}-${a.starttime}-${i}`}
                      a={a}
                      showDay={multiDay}
                      onOpen={onOpen}
                      onClose={onClose}
                      dimOf={dimOf}
                    />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HourSheet;
